import React, { useState } from 'react';
import { 
  Box, 
  Paper, 
  Typography, 
  TextField, 
  Button, 
  InputAdornment, 
  IconButton,
  Divider,
  useTheme,
  alpha,
  Link,
  FormControlLabel,
  Checkbox,
  CircularProgress
} from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { Visibility, VisibilityOff, Email, Lock } from '@mui/icons-material';
import { useThemeContext } from '../../context/ThemeContext';
import { useAuth } from '../../context/AuthContext';
import ThemeToggle from '../../components/ThemeToggle';

const Login: React.FC = () => {
  const navigate = useNavigate();
  const theme = useTheme();
  const { mode } = useThemeContext();
  const isDark = mode === 'dark';

  const [showPassword, setShowPassword] = useState(false);
  const [loginForm, setLoginForm] = useState({
    email: '',
    password: '',
    rememberMe: false
  });
  const [errors, setErrors] = useState({
    email: '',
    password: ''
  });
  const [isLoading, setIsLoading] = useState(false);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value, checked } = e.target;
    if (name === 'rememberMe') {
      setLoginForm({
        ...loginForm,
        [name]: checked
      });
    } else {
      setLoginForm({
        ...loginForm,
        [name]: value
      });
      // Clear error when typing
      if (errors[name as keyof typeof errors]) {
        setErrors({
          ...errors,
          [name]: ''
        });
      }
    }
  };

  const validateForm = (): boolean => {
    let valid = true;
    const newErrors = { ...errors };
    
    // Email validation
    if (!loginForm.email) {
      newErrors.email = 'Email is required';
      valid = false;
    } else if (!/\S+@\S+\.\S+/.test(loginForm.email)) {
      newErrors.email = 'Email is invalid';
      valid = false;
    } else {
      newErrors.email = '';
    }
    
    // Password validation
    if (!loginForm.password) {
      newErrors.password = 'Password is required';
      valid = false;
    } else if (loginForm.password.length < 6) {
      newErrors.password = 'Password must be at least 6 characters';
      valid = false;
    } else {
      newErrors.password = '';
    }
    
    setErrors(newErrors);
    return valid;
  };

  // Import the useAuth hook
  const { login } = useAuth();

  const handleLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (validateForm()) {
      setIsLoading(true);
      
      try {
        // Call the login function from AuthContext which uses the API
        const success = await login(loginForm.email, loginForm.password);
        
        if (success) {
          // Navigate to dashboard on successful login
          navigate('/');
        } else {
          // Show error message on failed login
          setErrors({
            email: 'Invalid email or password',
            password: 'Invalid email or password'
          });
        }
      } catch (error) {
        console.error('Login error:', error);
        setErrors({
          email: 'An error occurred during login',
          password: 'An error occurred during login'
        });
      } finally {
        setIsLoading(false);
      }
    }
  };

  return (
    <Box
      sx={{
        minHeight: '100vh',
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        backgroundColor: theme.palette.background.default,
        padding: { xs: 2, sm: 4 },
      }}
    >
      <Paper
        elevation={isDark ? 4 : 2}
        sx={{
          width: '100%',
          maxWidth: 450,
          borderRadius: 4,
          p: { xs: 3, sm: 4 },
          border: isDark 
            ? `1px solid ${alpha(theme.palette.primary.main, 0.1)}` 
            : `1px solid ${alpha(theme.palette.primary.main, 0.05)}`,
          boxShadow: isDark 
            ? '0 4px 30px rgba(0, 0, 0, 0.3)' 
            : '0 4px 20px rgba(0, 0, 0, 0.05)',
        }}
      >
        <Box sx={{ position: 'absolute', top: 20, right: 20 }}>
          <ThemeToggle />
        </Box>

        <Box sx={{ textAlign: 'center', mb: 4 }}>
          <Typography 
            variant="h4" 
            component="h1" 
            sx={{ 
              fontWeight: 700, 
              mb: 1, 
              color: theme.palette.primary.main,
              letterSpacing: '0.5px',
            }}
          >
            Gobla Admin Panel
          </Typography>
          <Typography 
            variant="body1" 
            color="textSecondary"
            sx={{ mb: 2 }}
          >
            Sign in to access your dashboard
          </Typography>
          <Divider />
        </Box>

        <Box component="form" onSubmit={handleLogin} sx={{ mt: 4 }}>
          <TextField
            fullWidth
            id="email"
            name="email"
            label="Email Address"
            margin="normal"
            variant="outlined"
            autoComplete="email"
            autoFocus
            value={loginForm.email}
            onChange={handleInputChange}
            error={!!errors.email}
            helperText={errors.email}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <Email color="primary" sx={{ opacity: 0.7 }} />
                </InputAdornment>
              ),
            }}
            sx={{
              borderRadius: 2,
              '& .MuiOutlinedInput-root': {
                borderRadius: 2,
              },
            }}
          />

          <TextField
            fullWidth
            id="password"
            name="password"
            label="Password"
            margin="normal"
            variant="outlined"
            type={showPassword ? 'text' : 'password'}
            autoComplete="current-password"
            value={loginForm.password}
            onChange={handleInputChange}
            error={!!errors.password}
            helperText={errors.password}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <Lock color="primary" sx={{ opacity: 0.7 }} />
                </InputAdornment>
              ),
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton
                    aria-label="toggle password visibility"
                    onClick={() => setShowPassword(!showPassword)}
                    edge="end"
                  >
                    {showPassword ? <VisibilityOff /> : <Visibility />}
                  </IconButton>
                </InputAdornment>
              ),
            }}
            sx={{
              borderRadius: 2,
              '& .MuiOutlinedInput-root': {
                borderRadius: 2,
              },
            }}
          />

          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mt: 1 }}>
            <FormControlLabel
              control={
                <Checkbox 
                  checked={loginForm.rememberMe} 
                  onChange={handleInputChange} 
                  name="rememberMe" 
                  color="primary" 
                />
              }
              label="Remember me"
            />
            <Link 
              href="#" 
              variant="body2" 
              sx={{ 
                color: theme.palette.primary.main,
                textDecoration: 'none',
                '&:hover': {
                  textDecoration: 'underline',
                },
              }}
            >
              Forgot password?
            </Link>
          </Box>

          <Button
            type="submit"
            fullWidth
            variant="contained"
            color="primary"
            size="large"
            disabled={isLoading}
            sx={{ 
              mt: 4, 
              mb: 2, 
              height: 48,
              fontWeight: 600,
              letterSpacing: '0.5px',
              textTransform: 'none',
              fontSize: '1rem',
              borderRadius: 2,
              boxShadow: `0 8px 16px ${alpha(theme.palette.primary.main, 0.2)}`,
              '&:hover': {
                boxShadow: `0 12px 20px ${alpha(theme.palette.primary.main, 0.3)}`,
              },
            }}
          >
            {isLoading ? <CircularProgress size={24} color="inherit" /> : 'Sign In'}
          </Button>

          <Box sx={{ textAlign: 'center', mt: 3 }}>
            <Typography variant="body2" color="textSecondary">
              Demo credentials: admin@jobgo.com / nothing
            </Typography>
          </Box>
        </Box>
      </Paper>
    </Box>
  );
};

export default Login;